import { Box, Grid, Typography, useTheme, Chip, Theme } from '@mui/material';
import { ApplicationProps } from '../../redux-store/types/api-types';

const StatusChip = ({ status, successText }: { status: string; successText?: string }) => {
  const theme = useTheme();
  const getColor = () => {
    switch (status?.toLowerCase()) {
      case 'success':
        return 'success';
      case 'verified':
        return 'info';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };
  return (
    <Chip
      label={successText || status}
      color={getColor()}
      sx={(theme: Theme) => ({ fontWeight: 700, textTransform: 'capitalize', ...theme.typography.body_500, })}
    />
  );
};

export default function StatusDetails({
  application,
}: {
  application: ApplicationProps;
}): JSX.Element {
  const theme = useTheme();
  const bseStatus = application?.isBSEEnabled
    ? `${application.bseClientActivationStatus ?? ''}`.toLowerCase() !== 'active'
      ? { status: 'pending', code: '', reason: application?.bseStarServiceResponse }
      : { status: 'success', code: '', reason: application?.bseStarServiceResponse }
    : { status: 'pending', code: '', reason: 'BSE not enabled' };

  const mpowerStatus = {
    status: application?.clientCode ? 'success' : 'pending',
    code: application?.clientCode ? application?.clientCode : application?.mpowerResponse && application?.mpowerResponse,
    reason: application?.mpowerResponse && application?.mpowerResponse
  };

  const consentOtpStatuses = application?.applicants?.map((applicant, index) => {
    const consentAtField: string = [
      'firstApplicantConsentAt',
      'secondApplicantConsentAt',
      'thirdApplicantConsentAt',
    ][index];

    const status = application[consentAtField as keyof ApplicationProps] !== null ? 'verified' : 'pending';

    return { holder: applicant?.name, status };
  });

  return (
    <Box
      sx={{
        bgcolor: 'white',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        borderRadius: '10px',
        border: `1px solid ${theme.palette.grey[100]}`,
        padding: '25px',
        mt: 5,
      }}
    >
      {/* Section Header */}
      <Typography variant="h6" sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
        Status Overview
      </Typography>

      {/* BSE & MPower */}
      <Grid container spacing={3} mb={4}>
        {[['BSE Star UCC', bseStatus], ['mPower Portfolio', mpowerStatus]].map(
          ([label, item]: any, idx) => (
            <Grid item xs={12} sm={6} key={idx}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.primary[50],
                  ...theme.typography.body_500,
                  display: 'flex',
                  flexDirection: 'column',
                  color: theme.palette.primary[900],
                  gap: 1,
                }}
              >
                <Typography variant="body_500" fontWeight={600}>
                  {label}
                </Typography>
                <StatusChip
                  status={item.status}
                  successText={item.code || item.status}
                />
                {(item.status.toLowerCase() === 'failed' || item.status.toLowerCase() === 'pending') && item.reason && (
                  <Typography variant="body2" color="error.main">
                    Reason: {item.reason}
                  </Typography>
                )}
              </Box>
            </Grid>
          )
        )}
      </Grid>

      {/* Consent OTP Status */}
      <Box>
        <Typography variant="h6" sx={(theme: Theme) => ({ fontSize: 18, fontWeight: 700, color: theme.palette.primary[900], mb: '25px' })}>
          Consent OTP Status
        </Typography>
        <Grid container spacing={2}>
          {consentOtpStatuses.map((item, index) => (
            <Grid item xs={12} sm={4} key={index}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  ...theme.typography.body_500,
                  backgroundColor: theme.palette.primary[50],
                  display: 'flex',
                  flexDirection: 'column',
                  color: theme.palette.primary[900],
                  gap: 1,
                }}
              >
                <Typography variant="body_400" fontWeight={700}>
                  {item.holder}
                </Typography>
                <StatusChip status={item.status} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
}
